import { useEffect, useMemo, useRef, useState } from "react";

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  borderRadius: 7,
  border: "1px solid #CBD5E1",
  marginTop: 5,
};
const buttonStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #CBD5E1",
  background: "#fff",
  color: "#183049",
  cursor: "pointer",
  fontWeight: 700,
};
const money = value => new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 2,
}).format(Number(value || 0));
const validMonth = value => /^\d{4}-(0[1-9]|1[0-2])$/.test(String(value || ""));
const parseGoal = value => {
  if (String(value).trim() === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0 || number > 1000000000) return null;
  return Math.abs(number * 100 - Math.round(number * 100)) < 0.00001 ? number : null;
};
const goalIsValid = value => parseGoal(value) !== null;
const valuesEqual = (a, b) => Number(a?.goal) === Number(b?.goal)
  && Number(a?.consultantGoal) === Number(b?.consultantGoal);

async function jsonRequest(url, options) {
  const response = await fetch(url, {
    credentials: "same-origin",
    cache: "no-store",
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body.ok === false) {
    throw new Error(body.error || `Não foi possível concluir (HTTP ${response.status})`);
  }
  return body;
}

export default function MonthlyGoalsEditor({
  defaultMonth,
  stores = [],
  canEdit = false,
  onSaved,
}) {
  const initialMonth = validMonth(defaultMonth) ? defaultMonth : "";
  const storeIds = JSON.stringify(stores.map(store => String(store.id)).sort());
  const [month, setMonth] = useState(initialMonth);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveErrors, setSaveErrors] = useState({});
  const [savedNotices, setSavedNotices] = useState({});
  const [savingStores, setSavingStores] = useState({});
  const baseline = useRef({});
  const requestSequence = useRef(0);
  const selectedMonth = useRef(month);
  selectedMonth.current = month;

  const dirtyStores = useMemo(() => stores.filter(store => {
    const id = String(store.id);
    return values[id] && baseline.current[id] && !valuesEqual(values[id], baseline.current[id]);
  }).map(store => String(store.id)), [stores, values]);
  const hasDirtyValues = dirtyStores.length > 0;

  useEffect(() => {
    if (!canEdit || !validMonth(month)) return undefined;
    const sequence = ++requestSequence.current;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setSaveErrors({});
    setSavedNotices({});
    jsonRequest(`/api/crm/monthly-goals?month=${encodeURIComponent(month)}`)
      .then(body => {
        if (cancelled || sequence !== requestSequence.current) return;
        const returnedStores = body.stores || {};
        const loaded = {};
        JSON.parse(storeIds).forEach(id => {
          const entry = returnedStores[id] || {};
          loaded[id] = {
            goal: entry.goal == null ? "" : String(entry.goal),
            consultantGoal: entry.consultantGoal == null ? "" : String(entry.consultantGoal),
            version: entry.version ?? 0,
          };
        });
        Object.entries(returnedStores).forEach(([id, entry]) => {
          if (loaded[String(id)]) return;
          loaded[String(id)] = {
            goal: entry.goal == null ? "" : String(entry.goal),
            consultantGoal: entry.consultantGoal == null ? "" : String(entry.consultantGoal),
            version: entry.version ?? 0,
          };
        });
        baseline.current = Object.fromEntries(Object.entries(loaded).map(([id, entry]) => [id, { ...entry }]));
        setValues(loaded);
      })
      .catch(error => {
        if (!cancelled && sequence === requestSequence.current) {
          baseline.current = {};
          setValues({});
          setLoadError(error.message || "Não foi possível carregar as metas.");
        }
      })
      .finally(() => {
        if (!cancelled && sequence === requestSequence.current) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [canEdit, month, storeIds]);

  if (!canEdit) return null;

  const changeMonth = event => {
    const nextMonth = event.target.value;
    if (!validMonth(nextMonth) || nextMonth === month) return;
    if (hasDirtyValues && !window.confirm("Há alterações de metas não salvas. Deseja descartá-las e mudar o mês?")) {
      event.target.value = month;
      return;
    }
    setMonth(nextMonth);
    setValues({});
    baseline.current = {};
  };

  const updateField = (storeId, field, value) => {
    setValues(current => ({
      ...current,
      [storeId]: { ...(current[storeId] || {}), [field]: value },
    }));
    setSaveErrors(current => ({ ...current, [storeId]: "" }));
    setSavedNotices(current => ({ ...current, [storeId]: "" }));
  };

  const saveStore = async store => {
    const storeId = String(store.id);
    const current = values[storeId];
    const goal = parseGoal(current?.goal);
    const consultantGoal = parseGoal(current?.consultantGoal);
    if (goal === null || consultantGoal === null) {
      setSaveErrors(errors => ({
        ...errors,
        [storeId]: "Informe metas maiores que zero, até R$ 1.000.000.000,00 e com no máximo duas casas decimais.",
      }));
      return;
    }
    setSavingStores(currentSaving => ({ ...currentSaving, [storeId]: true }));
    setSaveErrors(errors => ({ ...errors, [storeId]: "" }));
    setSavedNotices(notices => ({ ...notices, [storeId]: "" }));
    const saveMonth = month;
    const expectedVersion = current.version ?? 0;
    try {
      const body = await jsonRequest(`/api/crm/monthly-goals/${encodeURIComponent(saveMonth)}/${encodeURIComponent(storeId)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ goal, consultantGoal, expectedVersion }),
      });
      if (saveMonth !== selectedMonth.current) return;
      const savedValue = body.value || {};
      const updated = {
        goal: String(savedValue.goal ?? goal),
        consultantGoal: String(savedValue.consultantGoal ?? consultantGoal),
        version: savedValue.version ?? expectedVersion,
      };
      setValues(currentValues => ({
        ...currentValues,
        [storeId]: {
          ...currentValues[storeId],
          version: updated.version,
        },
      }));
      baseline.current = {
        ...baseline.current,
        [storeId]: { ...updated },
      };
      setSavedNotices(notices => ({ ...notices, [storeId]: "Metas salvas." }));
      onSaved?.({ month: saveMonth, storeId: store.id, value: savedValue });
    } catch (error) {
      if (saveMonth === selectedMonth.current) {
        setSaveErrors(errors => ({ ...errors, [storeId]: error.message || "Não foi possível salvar as metas." }));
      }
    } finally {
      if (saveMonth === selectedMonth.current) setSavingStores(currentSaving => ({ ...currentSaving, [storeId]: false }));
    }
  };

  const buttonDisabled = (storeId, row) => loading
    || !!savingStores[storeId]
    || !row
    || valuesEqual(row, baseline.current[storeId])
    || !goalIsValid(row.goal)
    || !goalIsValid(row.consultantGoal);

  return <section aria-labelledby="monthly-goals-title" style={{ marginBottom: 24 }}>
    <h2 id="monthly-goals-title" style={{ fontSize: 18, margin: "0 0 6px" }}>Metas mensais</h2>
    <div style={{ display: "flex", alignItems: "end", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
      <label style={{ fontWeight: 700, fontSize: 13 }}>
        Mês das metas
        <input
          aria-label="Mês das metas"
          type="month"
          value={month}
          onChange={changeMonth}
          style={{ ...inputStyle, width: 180 }}
        />
      </label>
      <span style={{ color: "#64748B", fontSize: 12 }}>A seleção vale somente para este editor.</span>
    </div>

    {loading && <p role="status" style={{ color: "#64748B" }}>Carregando metas…</p>}
    {loadError && <div style={{ marginBottom: 12 }}>
      <p role="alert" style={{ color: "#B91C1C", margin: "0 0 8px" }}>Erro ao carregar metas: {loadError}</p>
      <button type="button" style={buttonStyle} onClick={() => {
        setMonth(current => `${current}`);
        setLoading(true);
        setLoadError("");
        const sequence = ++requestSequence.current;
        jsonRequest(`/api/crm/monthly-goals?month=${encodeURIComponent(month)}`)
          .then(body => {
            if (sequence !== requestSequence.current) return;
            const returnedStores = body.stores || {};
            const loaded = Object.fromEntries(stores.map(store => {
              const entry = returnedStores[String(store.id)] || {};
              return [String(store.id), {
                goal: entry.goal == null ? "" : String(entry.goal),
                consultantGoal: entry.consultantGoal == null ? "" : String(entry.consultantGoal),
                version: entry.version ?? 0,
              }];
            }));
            Object.entries(returnedStores).forEach(([id, entry]) => {
              if (!loaded[String(id)]) loaded[String(id)] = {
                goal: entry.goal == null ? "" : String(entry.goal),
                consultantGoal: entry.consultantGoal == null ? "" : String(entry.consultantGoal),
                version: entry.version ?? 0,
              };
            });
            baseline.current = Object.fromEntries(Object.entries(loaded).map(([id, entry]) => [id, { ...entry }]));
            setValues(loaded);
          })
          .catch(error => {
            if (sequence === requestSequence.current) setLoadError(error.message || "Não foi possível carregar as metas.");
          })
          .finally(() => { if (sequence === requestSequence.current) setLoading(false); });
      }}>Tentar novamente</button>
    </div>}

    {!loading && !loadError && validMonth(month) && <div style={{ display: "grid", gap: 10 }}>
      {stores.map(store => {
        const storeId = String(store.id);
        const row = values[storeId];
        const busy = !!savingStores[storeId];
        return <article key={storeId} style={{
          border: "1px solid #D7E0E8",
          borderRadius: 10,
          padding: 12,
          background: "#F8FAFC",
        }}>
          <strong style={{ display: "block", marginBottom: 10 }}>{store.name || `Loja ${store.id}`}</strong>
          {row ? <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 700 }}>
              Meta da loja (R$)
              <input
                aria-label={`Meta da loja — ${store.name || store.id}`}
                type="number"
                min="0.01"
                max="1000000000"
                step="0.01"
                inputMode="decimal"
                value={row.goal}
                disabled={busy}
                onChange={event => updateField(storeId, "goal", event.target.value)}
                style={inputStyle}
              />
              <span style={{ display: "block", color: "#64748B", fontSize: 12, marginTop: 3 }}>Atual: {money(row.goal)}</span>
            </label>
            <label style={{ fontSize: 13, fontWeight: 700 }}>
              Meta por consultor (R$)
              <input
                aria-label={`Meta por consultor — ${store.name || store.id}`}
                type="number"
                min="0.01"
                max="1000000000"
                step="0.01"
                inputMode="decimal"
                value={row.consultantGoal}
                disabled={busy}
                onChange={event => updateField(storeId, "consultantGoal", event.target.value)}
                style={inputStyle}
              />
              <span style={{ display: "block", color: "#64748B", fontSize: 12, marginTop: 3 }}>Padrão individual por consultor</span>
            </label>
            <div style={{ display: "flex", alignItems: "end", gap: 8, flexWrap: "wrap" }}>
              <button type="button" style={buttonStyle} disabled={buttonDisabled(storeId, row)} onClick={() => saveStore(store)}>
                {busy ? "Salvando…" : "Salvar metas"}
              </button>
              {savedNotices[storeId] && <span role="status" style={{ color: "#15803D", fontWeight: 700, fontSize: 13 }}>{savedNotices[storeId]}</span>}
            </div>
          </div> : <p style={{ margin: 0, color: "#64748B", fontSize: 13 }}>Metas indisponíveis para esta loja.</p>}
          {saveErrors[storeId] && <p role="alert" style={{ margin: "8px 0 0", color: "#B91C1C" }}>{saveErrors[storeId]}</p>}
        </article>;
      })}
      {!stores.length && <p style={{ color: "#64748B" }}>Nenhuma loja disponível.</p>}
    </div>}
  </section>;
}
